const CONTEST_STATES = ['draft', 'upcoming', 'live', 'ended', 'finalized', 'cancelled'];

const isString = (value) => typeof value === 'string';
const isInt = (value) => Number.isInteger(value);
const isBool = (value) => typeof value === 'boolean';
const isDate = (value) => isString(value) && !Number.isNaN(Date.parse(value));
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const optionalString = (value) => (isString(value) ? value : null);

function decodeList(items, decoder) {
  if (!Array.isArray(items)) return null;
  const decoded = items.map(decoder);
  return decoded.every((item) => item !== null) ? decoded : null;
}

function decodeWinner(data) {
  if (!isObject(data) || !isInt(data.rank) || !isString(data.username) || !isInt(data.score)) {
    return null;
  }
  return { rank: data.rank, username: data.username, score: data.score };
}

export function decodeContestSummary(data) {
  if (
    !isObject(data) ||
    !isInt(data.id) ||
    !isString(data.name) ||
    !isString(data.description) ||
    !isDate(data.startTime) ||
    !isDate(data.endTime) ||
    !isInt(data.durationMinutes) ||
    !isInt(data.penaltyMinutes) ||
    !CONTEST_STATES.includes(data.state) ||
    !isBool(data.registered) ||
    !isInt(data.participants) ||
    !isInt(data.problemCount) ||
    !isInt(data.totalPoints) ||
    !(data.finalizedAt === null || isDate(data.finalizedAt))
  ) {
    return null;
  }

  const winners = decodeList(data.winners ?? [], decodeWinner);
  if (winners === null) return null;

  return {
    id: data.id,
    name: data.name,
    description: data.description,
    startTime: data.startTime,
    endTime: data.endTime,
    durationMinutes: data.durationMinutes,
    penaltyMinutes: data.penaltyMinutes,
    state: data.state,
    registered: data.registered,
    participants: data.participants,
    problemCount: data.problemCount,
    totalPoints: data.totalPoints,
    finalizedAt: data.finalizedAt,
    winners,
  };
}

export function decodeContestList(data) {
  if (!isObject(data) || !isDate(data.serverNow)) return null;
  const contests = decodeList(data.contests, decodeContestSummary);
  return contests === null ? null : { serverNow: data.serverNow, contests };
}

function decodeProblemSummary(data) {
  if (
    !isObject(data) ||
    !isInt(data.id) ||
    !isString(data.label) ||
    !isString(data.title) ||
    !isString(data.difficulty) ||
    !isInt(data.points) ||
    !isBool(data.solved) ||
    !isBool(data.attempted)
  ) {
    return null;
  }
  return {
    id: data.id,
    label: data.label,
    title: data.title,
    difficulty: data.difficulty,
    points: data.points,
    solved: data.solved,
    attempted: data.attempted,
  };
}

export function decodeContestDetail(data) {
  if (!isObject(data) || !isDate(data.serverNow)) return null;
  const contest = decodeContestSummary(data.contest);
  if (contest === null) return null;

  let problems = null;
  if (data.problems !== null) {
    problems = decodeList(data.problems, decodeProblemSummary);
    if (problems === null) return null;
  }
  return { serverNow: data.serverNow, contest, problems };
}

export function decodeContestProblem(data) {
  if (!isObject(data) || !isDate(data.serverNow)) return null;
  const problem = data.problem;
  if (
    !isObject(problem) ||
    !isInt(problem.id) ||
    !isString(problem.label) ||
    !isString(problem.title) ||
    !isString(problem.difficulty) ||
    !isInt(problem.points) ||
    !Array.isArray(problem.sampleTests)
  ) {
    return null;
  }

  return {
    serverNow: data.serverNow,
    problem: {
      id: problem.id,
      label: problem.label,
      title: problem.title,
      difficulty: problem.difficulty,
      points: problem.points,
      description: problem.description ?? {},
      sourceCode: isObject(problem.sourceCode) ? problem.sourceCode : {},
      sampleTests: problem.sampleTests.map((sample) => ({
        input: isString(sample?.input) ? sample.input : '',
        output: isString(sample?.output) ? sample.output : '',
      })),
    },
  };
}

function decodeLeaderboardRow(data) {
  if (
    !isObject(data) ||
    !isInt(data.rank) ||
    !isString(data.userId) ||
    !isString(data.username) ||
    !isInt(data.score) ||
    !isInt(data.finishTimeSeconds) ||
    !isInt(data.penaltyCount) ||
    !isInt(data.solvedCount)
  ) {
    return null;
  }
  return {
    rank: data.rank,
    userId: data.userId,
    username: data.username,
    score: data.score,
    finishTimeSeconds: data.finishTimeSeconds,
    penaltyCount: data.penaltyCount,
    solvedCount: data.solvedCount,
    problems: isObject(data.problems) ? data.problems : {},
    page: isInt(data.page) ? data.page : null,
  };
}

export function decodeLeaderboard(data) {
  if (
    !isObject(data) ||
    !isDate(data.serverNow) ||
    !CONTEST_STATES.includes(data.state) ||
    !isInt(data.page) ||
    !isBool(data.final) ||
    !isInt(data.totalParticipants) ||
    !isInt(data.pageSize) ||
    !isInt(data.pageCount) ||
    !(data.generatedAt === null || isDate(data.generatedAt))
  ) {
    return null;
  }

  const rows = decodeList(data.rows, decodeLeaderboardRow);
  if (rows === null) return null;
  const problems = decodeList(data.problems, (problem) =>
    isObject(problem) && isInt(problem.id) && isString(problem.label) && isInt(problem.points)
      ? { id: problem.id, label: problem.label, points: problem.points }
      : null
  );
  if (problems === null) return null;
  const me = data.me === null ? null : decodeLeaderboardRow(data.me);
  if (data.me !== null && me === null) return null;

  return {
    serverNow: data.serverNow,
    state: data.state,
    page: data.page,
    final: data.final,
    generatedAt: data.generatedAt,
    totalParticipants: data.totalParticipants,
    pageSize: data.pageSize,
    pageCount: data.pageCount,
    problemStats: isObject(data.problemStats) ? data.problemStats : {},
    problems,
    rows,
    me,
  };
}

export function decodeSubmissionRecord(data) {
  if (
    !isObject(data) ||
    !isString(data.id) ||
    !isString(data.status) ||
    !isString(data.verdict) ||
    !isDate(data.submittedAt) ||
    !isString(data.language)
  ) {
    return null;
  }
  return {
    id: data.id,
    status: data.status,
    verdict: data.verdict,
    stdout: optionalString(data.stdout),
    error: optionalString(data.error),
    executionTime: data.executionTime === null || data.executionTime === undefined ? null : String(data.executionTime),
    memoryUsed: isInt(data.memoryUsed) ? data.memoryUsed : null,
    failedTestCase: isInt(data.failedTestCase) ? data.failedTestCase : null,
    submittedAt: data.submittedAt,
    language: data.language,
    code: optionalString(data.code) ?? '',
    questionId: isInt(data.questionId) ? data.questionId : null,
  };
}

export function decodeContestSubmissions(data) {
  if (!isObject(data) || !isDate(data.serverNow)) return null;
  const submissions = decodeList(data.submissions, decodeSubmissionRecord);
  return submissions === null ? null : { serverNow: data.serverNow, submissions };
}

export function decodeSubmissionStatus(data) {
  if (!isObject(data) || !isString(data.status)) return null;
  return {
    status: data.status,
    verdict: optionalString(data.verdict),
    stdout: optionalString(data.stdout),
    error: optionalString(data.error),
    executionTime: data.executionTime === null || data.executionTime === undefined ? null : String(data.executionTime),
    memoryUsed: isInt(data.memoryUsed) ? data.memoryUsed : null,
    failedTestCase: isInt(data.failedTestCase) ? data.failedTestCase : null,
  };
}
